// Authentication Service
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail,
} from 'firebase/auth'
import { auth } from './firebase'

/**
 * Login with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User credential with uid
 */
export const loginWithEmailAndPassword = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    return {
      success: true,
      uid: userCredential.user.uid,
      user: userCredential.user,
    }
  } catch (error) {
    console.error('Error in loginWithEmailAndPassword:', error)
    let errorMessage = 'Error al iniciar sesión'

    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'Usuario no encontrado'
        break
      case 'auth/wrong-password':
        errorMessage = 'Contraseña incorrecta'
        break
      case 'auth/invalid-email':
        errorMessage = 'Email inválido'
        break
      case 'auth/user-disabled':
        errorMessage = 'Usuario deshabilitado'
        break
      case 'auth/too-many-requests':
        errorMessage = 'Demasiados intentos. Intenta más tarde'
        break
      default:
        errorMessage = error.message || 'Error al iniciar sesión'
    }

    return {
      success: false,
      error: errorMessage,
      code: error.code,
    }
  }
}

/**
 * Logout current user
 * @returns {Promise<Object>} Result of logout operation
 */
export const logout = async () => {
  try {
    await signOut(auth)
    return {
      success: true,
    }
  } catch (error) {
    console.error('Error in logout:', error)
    return {
      success: false,
      error: error.message || 'Error al cerrar sesión',
    }
  }
}

/**
 * Listen to auth state changes
 * @param {Function} callback - Callback function to execute on auth state change
 * @returns {Function} Unsubscribe function
 */
export const onAuthStateChange = callback => {
  return onAuthStateChanged(auth, callback)
}

/**
 * Get current user
 * @returns {Object|null} Current user or null
 */
export const getCurrentUser = () => {
  return auth.currentUser
}

/**
 * Get ID token for current user
 * @param {boolean} [forceRefresh=false] - Forzar renovación ante el Auth SDK (p. ej. reintento tras HTTP 401)
 * @returns {Promise<string|null>} ID token or null
 */
export const getIdToken = async (forceRefresh = false) => {
  try {
    const user = auth.currentUser
    if (user) {
      return await user.getIdToken(forceRefresh === true)
    }
    return null
  } catch (error) {
    console.error('Error getting ID token:', error)
    return null
  }
}

/**
 * Create a new user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User credential with uid
 */
export const createUserWithEmailAndPasswordService = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    return {
      success: true,
      uid: userCredential.user.uid,
      user: userCredential.user,
    }
  } catch (error) {
    console.error('Error in createUserWithEmailAndPasswordService:', error)
    let errorMessage = 'Error al crear usuario'

    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'El email ya está en uso'
        break
      case 'auth/invalid-email':
        errorMessage = 'Email inválido'
        break
      case 'auth/operation-not-allowed':
        errorMessage = 'Operación no permitida'
        break
      case 'auth/weak-password':
        errorMessage = 'La contraseña es muy débil. Debe tener al menos 6 caracteres'
        break
      default:
        errorMessage = error.message || 'Error al crear usuario'
    }

    return {
      success: false,
      error: errorMessage,
      code: error.code,
    }
  }
}

/**
 * Update user password
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Result of update operation
 */
export const updateUserPassword = async newPassword => {
  try {
    const user = auth.currentUser
    if (!user) {
      return {
        success: false,
        error: 'No hay usuario autenticado',
      }
    }

    await updatePassword(user, newPassword)
    return {
      success: true,
      message: 'Contraseña actualizada correctamente',
    }
  } catch (error) {
    console.error('Error in updateUserPassword:', error)
    let errorMessage = 'Error al actualizar contraseña'

    switch (error.code) {
      case 'auth/requires-recent-login':
        errorMessage =
          'Por seguridad, debes iniciar sesión nuevamente antes de cambiar la contraseña'
        break
      case 'auth/weak-password':
        errorMessage = 'La contraseña es muy débil. Debe tener al menos 6 caracteres'
        break
      default:
        errorMessage = error.message || 'Error al actualizar contraseña'
    }

    return {
      success: false,
      error: errorMessage,
      code: error.code,
    }
  }
}

/**
 * Reauthenticate user with current password
 * @param {string} password - Current password
 * @returns {Promise<Object>} Result of reauthentication
 */
export const reauthenticateUser = async password => {
  try {
    const user = auth.currentUser
    if (!user || !user.email) {
      return {
        success: false,
        error: 'No hay usuario autenticado',
      }
    }

    const credential = EmailAuthProvider.credential(user.email, password)
    await reauthenticateWithCredential(user, credential)
    return {
      success: true,
    }
  } catch (error) {
    console.error('Error in reauthenticateUser:', error)
    let errorMessage = 'Error al validar contraseña actual'

    switch (error.code) {
      case 'auth/wrong-password':
        errorMessage = 'La contraseña actual es incorrecta'
        break
      case 'auth/user-mismatch':
        errorMessage = 'Error de autenticación'
        break
      case 'auth/user-not-found':
        errorMessage = 'Usuario no encontrado'
        break
      default:
        errorMessage = error.message || 'Error al validar contraseña actual'
    }

    return {
      success: false,
      error: errorMessage,
      code: error.code,
    }
  }
}

/**
 * Send password reset email
 * @param {string} email - User email
 * @returns {Promise<Object>} Result of sending reset email
 */
export const sendPasswordReset = async email => {
  try {
    await sendPasswordResetEmail(auth, email)
    return {
      success: true,
      message: 'Se ha enviado un correo para restablecer tu contraseña',
    }
  } catch (error) {
    console.error('Error in sendPasswordReset:', error)
    let errorMessage = 'Error al enviar correo de recuperación'

    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'No existe una cuenta con este correo electrónico'
        break
      case 'auth/invalid-email':
        errorMessage = 'El correo electrónico no es válido'
        break
      case 'auth/too-many-requests':
        errorMessage = 'Demasiados intentos. Por favor intenta más tarde'
        break
      default:
        errorMessage = error.message || 'Error al enviar correo de recuperación'
    }

    return {
      success: false,
      error: errorMessage,
      code: error.code,
    }
  }
}
